<template>
    <header class="page-title">
        <h1><i class="bi bi-journal-text"></i> 日報一覧</h1>
        <router-link class="btn btn-primary" :to="{name: 'ReportAdd'}"><i class="bi bi-plus"></i> 新規入力</router-link>
    </header>

    <section class="section">
        <form class="row" @submit.prevent="fetch()">
            <div class="col-24 col-md-6 col-sm-8 mb-3 d-flex gap-3">
                <button type="button" class="btn btn-secondary" aria-label="前月" @click="fetchPrev()"><i class="bi bi-caret-left" aria-hidden="true"></i></button>
                <form-input-month v-model="month" required></form-input-month>
                <button type="submit" class="btn btn-primary">表示</button>
                <button type="button" class="btn btn-secondary" aria-label="翌月" @click="fetchNext()"><i class="bi bi-caret-right" aria-hidden="true"></i></button>
            </div>
            <div class="col-24 col-md-6 col-sm-8 mb-3">
                <form-select @change="fetch()" v-model="department" :options="departments" option_value="department_id" option_label="department_name" empty_option="全部門" :disabled="departments.length === 0"></form-select>
            </div>
        </form>
        <div class="table-responsive report-sticky-table">
        <table class="table table-bordered">
            <thead class="table-dark text-center sticky-header">
                <tr v-if="user_loading">
                    <th class="sticky-col"></th>
                    <th v-for="i in Array(user_count)" :key="i" scope="col"><text-loader :width="6"></text-loader></th>
                </tr>
                <tr v-else>
                    <th class="sticky-col"></th>
                    <th v-for="user of users" :key="user.user_id" scope="col">{{ user.user_name }}</th>
                </tr>
            </thead>
            <tbody class="text-center">
                <template v-for="date of this.dates" :key="date">
                    <tr v-if="user_loading">
                        <th class="sticky-col" scope="row">{{ $helper.mdw(date) }}</th>
                        <td v-for="i in Array(user_count)" :key="i"><text-loader :width="6"></text-loader></td>
                    </tr>
                    <tr v-else>
                        <th class="sticky-col" scope="row">{{ $helper.mdw(date) }}</th>
                        <td v-for="(user, user_index) of users" :key="user.user_id">
                            <template v-if="report_loading">
                                <text-loader :width="6"></text-loader>
                            </template>
                            <template v-else-if="user.user_id in report_map && date in report_map[user.user_id]">
                                <button type="button" class="btn btn-link p-0" @click="view(user_index, date)">
                                    {{ $helper.clock(report_map[user.user_id][date].work_time_total) }}
                                </button>
                            </template>
                            <template v-else>
                                -
                            </template>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        </div>
    </section>

    <part-view-report
        :report="view_report"
        :user="view_user"
        :date="view_date"
        :holiday="view_holiday"
        :loading="report_loading"
        :has_prev="has_prev_user"
        :has_next="has_next_user"
        @close="closeView()"
        @prev="viewPrevUser()"
        @next="viewNextUser()"
        @reload="reloadView()"
    ></part-view-report>
</template>

<script>
import FormInputMonth from '@/components/forms/FormInputMonth';
import FormSelect from '@/components/forms/FormSelect';
import TextLoader from '@/components/tools/TextLoader';
import PartViewReport from './PartViewReport'
import Report from "@/models/entities/report";
import User from "@/models/entities/user";
import Department from "@/models/entities/department";
import { datesOf, addMonth, subMonth } from '@/utilities/month'

export default {
    name: 'Report',
    components: {
        FormInputMonth,
        FormSelect,
        TextLoader,
        PartViewReport,
    },
    inject: [
        'startScreenLoading',
        'endScreenLoading',
        'showMessage',
        'showErrorMessage'
    ],
    data() {
        return {
            user_loading: true,
            report_loading: true,
            // month: thisMonth(), // computed に移動
            dates: [],
            users: [],
            user_count: 6,
            report_map: {},
            // 詳細パネルの表示対象 (users のインデックス + 日付)。日報未登録でも保持する
            view_user_index: null,
            view_date: null,
            departments: [],
        }
    },
    mounted() {
        this.fetch();

        this.$http.get('/list/department')
        .then((response) => {
            this.departments = response.data.map((row) => {return new Department(row)});
        })
    },
    methods: {
        fetch() {
            this.dates = datesOf(this.month);
            this.closeView();

            this.fetchUsers();
            this.fetchReports();
        },
        fetchUsers() {
            this.user_loading = true;
            this.users = [];
            this.$http.get('/list/user', {params: {month_from: this.month, month_to: this.month, department: this.department}})
            .then((response) => {
                this.users = response.data.map((row) => {return new User(row)});
                this.user_count = this.users.length;
                this.user_loading = false;
            });
        },
        fetchReports() {
            this.report_loading = true;
            this.report_map = {};
            this.$http.get(`/report/month/${this.month}`, {params: {department: this.department}})
            .then((response) => {
                for (let report of response.data) {
                    let user_id = report.user.user_id;
                    if (!(user_id in this.report_map)) {
                        this.report_map[user_id] = {};
                    }
                    this.report_map[user_id][report.report_date] = new Report(report);
                }
                this.report_loading = false;
            });
        },
        fetchPrev()
        {
            this.month = subMonth(this.month, 1);
            this.fetch();
        },
        fetchNext()
        {
            this.month = addMonth(this.month, 1);
            this.fetch();
        },
        view(user_index, date)
        {
            this.view_user_index = user_index;
            this.view_date = date;
        },
        closeView()
        {
            this.view_user_index = null;
            this.view_date = null;
        },
        // 表の左隣の列 (前の人) の日報へ。日報未登録でもスキップしない
        viewPrevUser()
        {
            if (this.has_prev_user) {
                this.view_user_index = this.view_user_index - 1;
            }
        },
        // 表の右隣の列 (次の人) の日報へ。日報未登録でもスキップしない
        viewNextUser()
        {
            if (this.has_next_user) {
                this.view_user_index = this.view_user_index + 1;
            }
        },
        // 表示中の日報を再読み込みする。ユーザー一覧は取り直さないのでパネルを開いたまま更新される
        reloadView()
        {
            this.fetchReports();
        },
    },
    computed: {
        // 表示中のユーザー (パネル非表示なら null)
        view_user() {
            if (this.view_user_index === null) {
                return null;
            }
            return this.users[this.view_user_index] ?? null;
        },
        // 表示中の日報 (未登録なら null)
        view_report() {
            if (!this.view_user || !this.view_date) {
                return null;
            }
            return this.report_map[this.view_user.user_id]?.[this.view_date] ?? null;
        },
        // 表示中のセルが休日として登録されているか (休日管理機能のない環境では常に false)
        view_holiday() {
            if (!this.view_user || !this.view_date) {
                return false;
            }
            return this.view_date in (this.status_map?.[this.view_user.user_id] ?? {});
        },
        has_prev_user() {
            return this.view_user_index !== null && this.view_user_index > 0;
        },
        has_next_user() {
            return this.view_user_index !== null && this.view_user_index < this.users.length - 1;
        },
        month: {
            get() {
                return this.$store.state.condition.month;
            },
            set(value) {
                this.$store.commit('condition/setMonth', value);
            },
        },
        department: {
            get() {
                return this.$store.state.condition.department;
            },
            set(value) {
                this.$store.commit('condition/setDepartment', value);
            },
        },
    },
}
</script>

<style scoped>
/* `.report-sticky-table` は本コンポーネント内で sticky 用 CSS の適用範囲を明示する
   専用クラス。scoped style に頼らず明示セレクタで絞ることで、scoped 解除時の
   事故予防 + 将来 DROPS-118 配下他子課題への共通化 lift 時の境界を明確化する。

   max-height: calc(100vh - 200px) の 200px 内訳:
   - navbar (position: fixed): 48px (body padding-top: 3rem)
   - page-title: 約 40px
   - form area (月切替 + 部門フィルタ, md 以上 1 段): 約 80px
   - section padding + 余白: 約 32px
   - 合計: 約 200px
   sm 未満は form が 2 段になるため別途 media query で 280px に調整。 */
.report-sticky-table {
    max-height: calc(100vh - 200px);
    overflow: auto;
    /* container 内スクロールが viewport 端に達したときにページ全体スクロールへ
       連鎖しないようにする (大きな表での触覚 UX 改善) */
    overscroll-behavior: contain;
}
@media (max-width: 767.98px) {
    .report-sticky-table {
        /* sm 未満は form area が 2 段に折り返るため余白を増やす */
        max-height: calc(100vh - 280px);
    }
}

/* border-collapse: collapse のままだと、cell 間の collapsed border は cell の外側
   (cell 間スペース) に描かれ、sticky cell に追従しない。scroll 時に thead 底辺の
   seam がボディ側 border 色 (薄い灰) に置き換わる現象の根本原因。
   separate + border-spacing: 0 で各 cell に独立した border を持たせ、sticky と
   一緒に border も move するようにする。table-bordered の border-width: 1px 0 /
   0 1px ルールを打ち消し、cell ごとに top + left border を持たせて格子状を再現
   (最外周のみ table 側で補完)。 */
.report-sticky-table > .table {
    border-collapse: separate;
    border-spacing: 0;
}
.report-sticky-table > .table-bordered > :not(caption) > * {
    border-width: 0;
}
.report-sticky-table > .table-bordered > :not(caption) > * > * {
    /* 各 cell に top + left border のみ。右端/下端は table 自身の
       border-right/bottom で補完するため、ここでは top + left のみで充分 */
    border-width: 1px 0 0 1px;
    border-style: solid;
    /* separate では border-color が継承されず currentColor になるため明示 */
    border-color: var(--bs-border-color, #dee2e6);
}
.report-sticky-table > .table-bordered {
    /* 全体の右端/下端 border を table 自身が持つ */
    border-right: 1px solid var(--bs-border-color, #dee2e6);
    border-bottom: 1px solid var(--bs-border-color, #dee2e6);
}

/* 縦スクロール時にヘッダ行を container の top に固定。
   z-index 2/3/4 は scroll container 内に閉じた値 (Bootstrap zindex-stack の
   dropdown 1000 / sticky 1020 / fixed 1030 / offcanvas 1045 / modal 1055 とは
   別 stacking context、scroll container の中で完結するため低い値で充分)。
   sticky-col(2) < sticky-header(3) < 左上角(4) の階層で重なり順を保証する。 */
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 3;
}
.report-sticky-table > .table-bordered > .sticky-header > tr > th {
    /* specificity (0,4,4) を満たすセレクタで、上記の border-width: 0 ルール
       (0,4,3) と border-width: 1px 0 0 1px ルール (0,4,4) を !important なしで
       上書きする */
    background-color: var(--bs-table-bg, #212529);
    border-color: #373b3e;
    border-bottom: 1px solid #373b3e;
    /* ユーザー名が縦書きに潰れるのを防ぐ */
    white-space: nowrap;
}

/* 横スクロール時に左端の日付列を container の left に固定 */
.sticky-col {
    position: sticky;
    left: 0;
    z-index: 2;
    background-color: var(--bs-body-bg, #fff);
    white-space: nowrap;
}

/* thead の左上角セルは縦横両方の sticky 領域より上に表示する (z-index 4) */
.sticky-header .sticky-col {
    z-index: 4;
}

/* tbody の日付列 (scope="row" のため <th>) は太字化を解除し、元の <td> と同じ見た目を保つ */
.report-sticky-table > .table > tbody > tr > th.sticky-col {
    font-weight: normal;
}

/* 印刷時は sticky を解除して全行を順次印刷する */
@media print {
    .report-sticky-table {
        max-height: none;
        overflow: visible;
    }
    .sticky-header,
    .sticky-col {
        position: static;
    }
}

/* thead の左上角セルは縦横両方の sticky 領域より上に表示する */
.sticky-header .sticky-col {
    background-color: var(--bs-table-bg, #212529);
    z-index: 4;
}
</style>
